// SPDX-FileCopyrightText: Copyright 2025 OP Financial Group (https://op.fi). All Rights Reserved.
//
// SPDX-License-Identifier: LicenseRef-OpAllRightsReserved

import React, { useLayoutEffect, useEffect, useState, useMemo, useRef } from 'react';
import TupasLogin from './pages/tupasLogin/TupasLogin';
import ErrorPage from './pages/ErrorPage';
import { HashRouter, Routes, Route } from 'react-router';
import FirstPage from './pages/firstPage/FirstPage';
import PreviewAndConfirmationPage from './pages/previewAndConfirmation/PreviewAndConfirmationPage';
import {
    ContextWrapper,
    defaultContractData,
    ContractData,
    AdminData,
    AdminAgreementType,
    Role,
    ApiResponse,
    AdminRolesData,
    AdminDataBackend,
    CompanyDataBackend,
} from './context/ContextWrapper';
import { pageRoutes } from './Constants';
import ThankYou from './pages/ThankYou';
import ErrorBoundary from './components/ErrorBoundary';
import ManualSigning from './pages/manualSigning/ManualSigning';
import { Loading, LocaleProvider } from '@op/opds.opux';
import SessionTimeoutWarningModal from './components/SessionTimeoutWarningModal';
import OpLogo from './components/OpLogo';
import { handleRedirectionTimeout, handleWindowBeforeUnload } from './Session';
import { deepCopy, getRolesForAgreementType } from './util/Util';
import client from './api/RestApi';

let warningTimer: any = null;
let redirectionTimer: any = null;

const locale: string = (document.documentElement.getAttribute('lang') ?? 'fi').substring(0, 2).toLowerCase();

const App: React.FC = () => {
    const [contractData, setContractData] = useState<ContractData>(defaultContractData);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [showAgreementTypes, setShowAgreementTypes] = useState<boolean>(false);
    const [isDataSavingInProgress, setIsDataSavingInProgress] = useState<boolean>(false);
    const [showAlertAfterTupas, setShowAlertAfterTupas] = useState<boolean>(true);
    const [showSessionTimeoutWarningModal, setShowSessionTimeoutWarningModal] = useState<boolean>(false);
    const hasInitialized = useRef(false);

    useEffect(() => {
        // Set up an interval to periodically send a request to the backend.
        // This is necessary to keep the CSRF token valid, as it becomes invalid
        // if there is no traffic between the frontend and backend for a certain period.
        const intervalId = setInterval(() => {
            client.get('/health').catch(() => {});
        }, 240000);

        return () => clearInterval(intervalId);
    }, []);

    const isAdminFlow = useMemo<boolean>(() => {
        const query = new URLSearchParams(window.location.search);
        // @ts-ignore
        for (const key of query.keys()) {
            if (key === 'admin') {
                return true;
            }
        }
        return false;
    }, []);

    useLayoutEffect(() => {
        // Skip mount in development mode to prevent duplicate calls
        if (process.env.NODE_ENV === 'development') {
            // Use a flag to prevent double execution in React StrictMode
            if (hasInitialized.current) return;
            hasInitialized.current = true;
        }

        warningTimer = setTimeout(
            () => setShowSessionTimeoutWarningModal(true),
            Number(document.getElementById('modalWindowWaitTime')?.getAttribute('value'))
        );
        redirectionTimer = setTimeout(
            () => handleRedirectionTimeout(),
            Number(document.getElementById('redirectionWaitTime')?.getAttribute('value'))
        );

        isAdminFlow ? getDataFromServerSessionAdminFlow() : getDataFromServerSession();

        findPage();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        window.addEventListener('beforeunload', handleWindowBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleWindowBeforeUnload);
            if (warningTimer) clearTimeout(warningTimer);
            if (redirectionTimer) clearTimeout(redirectionTimer);
        };
    }, []);

    const updateInformation = (data: ContractData): void => {
        setContractData(data);
    };

    const getDataFromServerSession = (): void => {
        getDataFromServer('/company-information');
    };

    const getDataFromServerSessionAdminFlow = (): void => {
        getDataFromServer('/company-information-admin-flow');
    };

    const getDataFromServer = (url: string): void => {
        if (isLoading) {
            client
                .get(url)
                .then((response: ApiResponse) => {
                    if (response.status === 'FAILURE') {
                        setIsLoading(false);
                        window.location.hash = pageRoutes.error;
                    } else {
                        const dataFromSession: CompanyDataBackend = response.body ? JSON.parse(response.body) : {};
                        let showAlert: boolean = false;
                        if (dataFromSession) {
                            setDataToContext(dataFromSession);
                        } else {
                            const params = new URLSearchParams(window.location.search);
                            showAlert = params.get('tupasReturn') === 'true';
                        }
                        setIsLoading(false);
                        setShowAlertAfterTupas(showAlert);
                    }
                })
                .catch(() => {
                    setIsLoading(false);
                    window.location.hash = pageRoutes.error;
                });
        }
    };

    const setDataToContext = (dataFromSession: CompanyDataBackend): void => {
        const copyOfContractData = deepCopy(contractData);
        Object.assign(copyOfContractData.companyData, dataFromSession);
        setAdminData(copyOfContractData.administrators, dataFromSession);
        Object.assign(copyOfContractData.confirmSignatory, dataFromSession);
        updateInformation(copyOfContractData);
    };

    const setAdminData = (administrators: AdminData[], dataFromSession: CompanyDataBackend): void => {
        administrators.length = dataFromSession.administrators ? 0 : administrators.length;
        dataFromSession.administrators?.forEach((admin: AdminDataBackend) => {
            const newAdmin: AdminData = {
                firstName: admin.firstName,
                lastName: admin.lastName,
                personalIdentityCode: admin.personalIdentityCode,
                adminPhoneNumber: admin.adminPhoneNumber,
                adminEmailAddress: admin.adminEmailAddress,
                canEditRoles: admin.canEditRoles,
                agreementTypes: getAdminAgreementTypes(admin),
            };
            administrators.push(newAdmin);
        });
    };

    const getAdminAgreementTypes = (adminData: AdminDataBackend): Record<string, AdminRolesData> => {
        const agreementTypes: Record<string, any> = {};
        adminData.adminAgreementTypes.forEach((agreementType: AdminAgreementType) => {
            const newRoles = getRolesForAgreementType(agreementType.agreementType as string);
            newRoles.forEach((newRole: Role) => {
                checkPreviouslySelectedRoles(newRole, agreementType.roles);
            });
            agreementTypes[agreementType.agreementType as string] = {
                rolesData: {
                    roles: newRoles,
                    allRolesSelected: agreementType.allRolesSelected,
                },
            };
        });
        return agreementTypes;
    };

    const checkPreviouslySelectedRoles = (newRole: Role, previousRoles: Role[]): void => {
        previousRoles.forEach((previousRole) => {
            if (newRole.name === previousRole.name && (newRole.heading ?? null) === previousRole.heading) {
                newRole.isChecked = true;
            }
        });
    };

    const findPage = (): void => {
        const params = new URLSearchParams(window.location.search);
        window.location.hash = params.get('code') ? pageRoutes.thankYou : handleTupasReturn(params.get('error'));
    };

    const handleTupasReturn = (error: any): string => {
        return error ? pageRoutes.tupasLogin : pageRoutes.firstPage;
    };

    const setPreviewContinuePath = (): string => {
        const selectedSigning = contractData.confirmSignatory.selectedSigning;
        return selectedSigning === 'tupas' ? pageRoutes.tupasLogin : pageRoutes.manualSigning;
    };

    const contextValue = useMemo(
        () => ({
            isAdminFlow,
            showAgreementTypes,
            contractData,
            isDataSavingInProgress,
            updateInformation,
            setShowAgreementTypes,
            setIsDataSavingInProgress,
        }),
        [isAdminFlow, showAgreementTypes, contractData, isDataSavingInProgress]
    );

    if (isLoading) {
        return (
            <div className="opux-spacing-top-margin-double opux-align-center">
                <Loading />
            </div>
        );
    }

    return (
        <HashRouter>
            <ContextWrapper.Provider value={contextValue}>
                <OpLogo />
                <SessionTimeoutWarningModal
                    closeModal={() => setShowSessionTimeoutWarningModal(false)}
                    isOpen={showSessionTimeoutWarningModal}
                />
                <div id="e-service-content" className="opds">
                    <LocaleProvider locale={locale}>
                        <ErrorBoundary>
                            <Routes>
                                <Route
                                    path={pageRoutes.firstPage}
                                    element={
                                        <FirstPage
                                            continuePath={pageRoutes.infoPreview}
                                            showAlert={showAlertAfterTupas}
                                        />
                                    }
                                />
                                <Route
                                    path={pageRoutes.infoPreview}
                                    element={
                                        <PreviewAndConfirmationPage
                                            backPath={pageRoutes.firstPage}
                                            continuePath={setPreviewContinuePath()}
                                        />
                                    }
                                />
                                <Route path={pageRoutes.manualSigning} element={<ManualSigning />} />
                                <Route
                                    path={pageRoutes.tupasLogin}
                                    element={
                                        <TupasLogin
                                            originUrl={
                                                window.location.origin +
                                                window.location.pathname +
                                                window.location.search
                                            }
                                        />
                                    }
                                />
                                <Route path={pageRoutes.thankYou} element={<ThankYou />} />
                                <Route path={pageRoutes.error} element={<ErrorPage />} />
                            </Routes>
                        </ErrorBoundary>
                    </LocaleProvider>
                </div>
            </ContextWrapper.Provider>
        </HashRouter>
    );
};

App.displayName = 'App';
export default App;
